import * as React from 'react'
import { cn } from '../../lib/utils'
import { CloseButton } from './close-button'

/**
 * FilterChip — a single applied filter shown as a removable pill above a table or
 * list (the "active filters" bar that appears once filter popovers are applied).
 * A muted `label` prefix (the field) plus an emphasized `value`, with a round ×
 * to clear it. Pass `onClick` to make the body re-open the filter for editing —
 * the chip then has two targets (body = edit, × = remove), like a link cell with
 * a preview button.
 *
 *   <FilterChip label="Cuisine" value="Italian" onRemove={clear} />
 *   <FilterChip label="City" value="3 selected" onClick={edit} onRemove={clear} />
 *
 * Reach for the `value`-only form (no `label`) for self-describing filters such as
 * a saved-search name or a free-text query.
 */
export interface FilterChipProps {
  /** The filter field name, rendered as a muted `label:` prefix. Optional. */
  label?: React.ReactNode
  /** The applied value(s) — e.g. "Italian", "3 selected", "50+". */
  value: React.ReactNode
  /** Re-open the filter for editing; makes the chip body a button. */
  onClick?: () => void
  /** Clear this filter; renders the × when provided. */
  onRemove?: () => void
  /** Accessible label for the × (defaults to "Remove {label} filter"). */
  removeLabel?: string
  className?: string
}

export const FilterChip = ({
  label,
  value,
  onClick,
  onRemove,
  removeLabel,
  className,
}: FilterChipProps) => {
  const body = (
    <>
      {label != null && <span className="text-fbui-gray-500">{label}:</span>}
      <span className="font-medium text-fbui-gray-900">{value}</span>
    </>
  )
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md border border-fbui-gray-300 bg-fbui-base-white pl-2 text-xs text-fbui-gray-700',
        onRemove ? 'pr-1' : 'pr-2',
        className
      )}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className="flex items-center gap-1 py-1 outline-none transition-colors hover:text-fbui-gray-900 focus-visible:underline"
        >
          {body}
        </button>
      ) : (
        <span className="flex items-center gap-1 py-1">{body}</span>
      )}
      {onRemove && (
        <CloseButton
          shape="round"
          size="xs"
          onClick={onRemove}
          aria-label={
            removeLabel ??
            (typeof label === 'string' ? `Remove ${label} filter` : 'Remove filter')
          }
        />
      )}
    </span>
  )
}
FilterChip.displayName = 'FilterChip'
